import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

export const FloatingLanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-8 right-8 z-50" data-testid="floating-language-switcher">
      <div className="relative">
        {/* Expanded Language List */}
        {isOpen && (
          <div className="absolute bottom-20 right-0 bg-white rounded-2xl shadow-2xl border-2 border-[hsl(215,75%,16%)] p-2 min-w-[200px] animate-in slide-in-from-bottom-4 fade-in duration-200">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  i18n.language === lang.code
                    ? 'bg-[hsl(215,75%,16%)] text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
                data-testid={`floating-lang-${lang.code}`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="font-semibold">{lang.name}</span>
                {i18n.language === lang.code && (
                  <svg className="w-5 h-5 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Toggle Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-3 bg-[hsl(215,75%,16%)] text-white px-6 py-4 rounded-full shadow-2xl hover:bg-[hsl(224,64%,33%)] transition-all duration-300 hover:scale-105"
          data-testid="floating-lang-toggle"
        >
          <Globe className="w-6 h-6" />
          <span className="font-bold text-lg">{currentLanguage.flag}</span>
          <span className="font-semibold hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
      </div>
    </div>
  );
};